'use client';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import { MapPin } from 'lucide-react';
import type { BusinessLocationsMarker } from '@/components/maps/BusinessLocationsMap';
import { cn } from '@/components/ui/utils';

type PlaceHubMarker = BusinessLocationsMarker & {
  menuHref: string;
  detailsHref: string;
};

const BusinessLocationsMap = dynamic(() => import('@/components/maps/BusinessLocationsMap'), {
  ssr: false,
  loading: () => <div className="flex h-full items-center justify-center bg-[var(--color-bg-sunken)] text-sm text-[var(--color-text-secondary)]">Loading map…</div>,
});

export default function PlaceHubV2Map({
  markers,
  selectedId,
  onSelect,
  mobileMapOpen,
  unmappedCount,
}: {
  markers: PlaceHubMarker[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  mobileMapOpen: boolean;
  unmappedCount: number;
}) {
  if (markers.length === 0) return null;
  const selectedMarker = markers.find((marker) => marker.id === selectedId) ?? null;
  return (
    <div className={cn('lg:sticky lg:top-6 lg:order-none', mobileMapOpen ? 'order-first' : 'hidden lg:block')}>
      <section
        id="place-hub-location-map"
        aria-label="Branch locations on map"
        className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-raised)]"
      >
        <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-4 py-3 text-sm font-semibold text-[var(--color-text-primary)]">
          <MapPin className="size-4 text-[var(--color-accent)]" aria-hidden="true" />
          Location map
        </div>
        <div className="h-64 sm:h-80">
          <BusinessLocationsMap markers={markers} selectedId={selectedId} onSelect={onSelect} route={null} userLocation={null} fitSignal={markers.map((marker) => marker.id).join(':')} />
        </div>
        <div className="space-y-3 px-4 py-3">
          {selectedMarker ? (
            <div aria-live="polite" className="flex flex-wrap items-center justify-between gap-3">
              <p className="min-w-0 text-sm font-semibold text-[var(--color-text-primary)]">
                Selected: {selectedMarker.name}
              </p>
              <div className="flex shrink-0 items-center gap-3">
                <Link href={selectedMarker.detailsHref} className="inline-flex min-h-10 items-center text-sm font-semibold text-[var(--color-text-secondary)] underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]">
                  Details
                </Link>
                <Link href={selectedMarker.menuHref} className="inline-flex min-h-10 items-center gap-1 text-sm font-semibold text-[var(--color-accent)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]">
                  View location <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          ) : (
            <p className="text-xs text-[var(--color-text-secondary)]">Choose a pin or numbered location.</p>
          )}
          {unmappedCount > 0 ? (
            <p className="text-xs text-[var(--color-text-secondary)]">
              {unmappedCount} {unmappedCount === 1 ? 'location does' : 'locations do'} not have map coordinates yet; all locations remain available in the list.
            </p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
